import { ArrowLeftIcon, CogIcon, DocumentMagnifyingGlassIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { formatDate } from '../../../../common-utils/formatters';
import type { Company } from '../../../@types/Company';
import type { FixedNewsletter, NewsletterType } from '../../../@types/Newsletter';
import { NEWSLETTER_TYPE_LABELS } from '../../../@types/Newsletter';
import type { RootState } from '../../../store/store';
import classNames from '../../../utils/classNames';
import { JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import MUITable from '../../common/MUITable';
import { showToast } from '../../common/Notification';
import MiniNewsletterPreview from './components/MiniNewsletterPreview';

const FILTER_OPTIONS: { label: string; value: NewsletterType | 'all' }[] = [
  { label: 'Tous', value: 'all' },
  { label: 'DPE', value: 'dpe' },
  { label: 'Vente', value: 'sell' },
  { label: 'Travaux', value: 'works' },
  { label: 'Vivre ensemble', value: 'legal' },
  { label: 'Location', value: 'rental' },
];

function NewsletterList() {
  const navigate = useNavigate();
  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company) as Company;

  const [newsletters, setNewsletters] = useState<FixedNewsletter[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<NewsletterType | 'all'>('all');

  const fetchNewsletters = async() => {
    if (!jwt || !company?.id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      
      const response = await fetch(`/api/companies/${company.id}/fixed-newsletters`, JWTHeader(jwt));
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Ensure data is an array
      const newslettersArray = Array.isArray(data) ? data : [];
      setNewsletters(newslettersArray);
    } catch (error) {
      console.error('Failed to fetch newsletters:', error);
      showToast('error', 'Une erreur est survenue lors du chargement des newsletters.');
      setNewsletters([]); // Set empty array on error
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchNewsletters();
  }, [jwt, company?.id]);

  const handleViewNewsletter = useCallback((newsletter: FixedNewsletter) => {
    // Use the company's preferred template if available, otherwise use the first template
    const preferredTemplate = company?.brandingSettings?.defaultTemplate;
    const templateToUse = preferredTemplate && newsletter.templates.some(t => t.name === preferredTemplate)
      ? preferredTemplate
      : newsletter.templates[0]?.name;

    if (templateToUse) {
      const newsletterId = `fixed:${newsletter.date}:${newsletter.slug}:${templateToUse}`;
      navigate(ROUTES.COMMUNICATION_NEWSLETTER_PREVIEW.replace(':id', newsletterId));
    }
  }, [company?.brandingSettings?.defaultTemplate, navigate]);

  const filteredNewsletters = useMemo(() => {
    if (activeFilter === 'all') {return newsletters;}
    return newsletters.filter((n) => n.type === activeFilter);
  }, [newsletters, activeFilter]);

  const filterCounts = useMemo(() => {
    const counts: Record<string, number> = { all: newsletters.length };
    for (const n of newsletters) {
      counts[n.type] = (counts[n.type] || 0) + 1;
    }
    return counts;
  }, [newsletters]);

  const headers = useMemo(() => ([
    { title: 'Aperçu' },
    { title: 'Titre' },
    { title: 'Type' },
    { title: 'Disponible depuis' },
    { title: 'Statut' },
    { title: 'Actions', align: 'right' as const },
  ]), []);

  const tableData = useMemo(() => filteredNewsletters.map((newsletter) => {
    const preferredTemplate = company?.brandingSettings?.defaultTemplate;
    const templateToUse = preferredTemplate && newsletter.templates.some(t => t.name === preferredTemplate)
      ? preferredTemplate
      : newsletter.templates[0]?.name;

    const newsletterTitle = newsletter.title || newsletter.slug.replaceAll('-', ' ');

    const canPreview = Boolean(templateToUse && company?.brandingSettings);

    return {
      'Aperçu': canPreview && templateToUse ? (
        <MiniNewsletterPreview
          date={newsletter.date}
          slug={newsletter.slug}
          templateName={templateToUse}
          jwt={jwt}
          company={company}
        />
      ) : (
        <div className="flex h-full items-center justify-center px-2 text-center text-xs text-gray-500">
          Sélectionnez un modèle pour afficher l'aperçu.
        </div>
      ),
      'Titre': newsletterTitle,
      'Type': NEWSLETTER_TYPE_LABELS[newsletter.type] ?? newsletter.type,
      'Disponible depuis': newsletter.availableDate ? formatDate(newsletter.availableDate) : '-',
      'Statut': newsletter.sentAt ? `Marquée envoyée le ${formatDate(newsletter.sentAt)}` : 'Pas encore envoyée',
      'Actions': (
        <div className="flex flex-wrap justify-end gap-2">
          <button
            onClick={() => handleViewNewsletter(newsletter)}
            className="flex items-center justify-center gap-1 rounded border border-rgpd-blue px-3 py-1 text-sm text-rgpd-blue transition-colors hover:bg-rgpd-blue hover:text-white"
            disabled={!newsletter.templates || newsletter.templates.length === 0}
          >
            <DocumentMagnifyingGlassIcon className="h-4 w-4" />
            <span>Voir</span>
          </button>
        </div>
      ),
    };
  }), [company, handleViewNewsletter, jwt, filteredNewsletters]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="rounded-lg bg-white p-6 shadow-md">
          <div className="mb-8 flex items-center">
            <div className="flex items-center gap-2 text-rgpd-blue">
              <ArrowLeftIcon className="h-5 w-5" />
              <span>Retour</span>
            </div>
          </div>

          <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <h1 className="text-2xl font-bold">Newsletters</h1>
              <p className="mt-2 text-gray-600">
                Chargement des newsletters disponibles...
              </p>
            </div>
            <div className="flex w-full flex-col items-stretch gap-2 md:w-auto md:flex-row md:items-center">
              <div className="h-10 w-full animate-pulse rounded-lg bg-gray-200 px-6 py-2 md:w-56" />
              <div className="h-10 w-full animate-pulse rounded-lg bg-gray-200 px-6 py-2 md:w-56" />
            </div>
          </div>

          <div className="animate-pulse">
            <div className="mb-4 h-4 w-48 rounded bg-gray-200"></div>
            <div className="space-y-3">
              <div className="h-12 rounded bg-gray-200"></div>
              <div className="h-10 rounded bg-gray-100"></div>
              <div className="h-10 rounded bg-gray-100"></div>
              <div className="h-10 rounded bg-gray-100"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg bg-white p-6 shadow-md">
        <div className="mb-8 flex items-center">
          <button
            onClick={() => navigate(ROUTES.DASHBOARD)}
            className="flex items-center gap-2 text-rgpd-blue transition-colors hover:text-rgpd-blue-dark"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            <span>Retour</span>
          </button>
        </div>

        <div className="mb-4 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-2xl font-bold">Newsletters</h1>
            <p className="mt-2 text-gray-600">
              Consultez les newsletters disponibles pour votre entreprise.
            </p>
          </div>
          <div className="flex w-full flex-col items-stretch gap-2 md:w-auto md:flex-row md:items-center">
            <button
              onClick={() => navigate(ROUTES.COMMUNICATION_UNSUBSCRIBERS)}
              className="w-full rounded-lg border border-rgpd-blue px-6 py-2 text-rgpd-blue transition-colors hover:bg-rgpd-blue hover:text-rgpd-white md:w-auto"
            >
              Gérer les désinscriptions
            </button>
            <button
              onClick={() => navigate(ROUTES.COMMUNICATION_NEWSLETTER_TEMPLATE_SELECTION)}
              className="flex w-full items-center gap-2 rounded-lg bg-rgpd-blue px-6 py-2 text-white transition-colors hover:bg-rgpd-blue-dark md:w-auto"
            >
              <CogIcon className="h-5 w-5" />
              Modèle de newsletter
            </button>
          </div>
        </div>

        <div className="mb-6 flex items-center gap-2.5 rounded-md border border-blue-200 bg-blue-50 px-4 py-2.5">
          <InformationCircleIcon className="h-5 w-5 shrink-0 text-blue-500" />
          <p className="text-sm text-blue-700">
            Les newsletters sont à envoyer depuis votre propre logiciel d'envoi de mail. Consultez-les ici puis importez-les dans votre outil d'emailing.
          </p>
        </div>

        {newsletters.length === 0 ? (
          <div className="py-8 text-center">
            <InformationCircleIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
            <p className="mb-2 text-lg text-gray-600">Aucune newsletter disponible</p>
            <p className="text-sm text-gray-500">
              Les newsletters seront affichées ici une fois qu'elles seront disponibles.
            </p>
          </div>
        ) : (
          <div className="relative">
            {/* Type filter pills */}
            <div className="mb-5 flex flex-wrap items-center gap-2">
              {FILTER_OPTIONS.map(({ label, value }) => {
                const isActive = activeFilter === value;
                const count = filterCounts[value] ?? 0;

                return (
                  <button
                    key={value}
                    onClick={() => setActiveFilter(value)}
                    className={classNames(
                      'relative inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm font-medium transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-rgpd-blue focus-visible:ring-offset-2',
                      isActive
                        ? 'border-rgpd-blue bg-rgpd-blue text-white shadow-sm'
                        : 'border-gray-300 bg-white text-gray-600 hover:border-rgpd-blue hover:text-rgpd-blue',
                    )}
                    aria-pressed={isActive}
                  >
                    {label}
                    <span
                      className={classNames(
                        'inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1 text-xs font-semibold leading-none',
                        isActive
                          ? 'bg-white/25 text-white'
                          : 'bg-gray-100 text-gray-500',
                      )}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {filteredNewsletters.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 py-12">
                <InformationCircleIcon className="mb-3 h-10 w-10 text-gray-400" />
                <p className="text-sm font-medium text-gray-600">
                  Aucune newsletter de type « {activeFilter !== 'all' ? NEWSLETTER_TYPE_LABELS[activeFilter] : ''} »
                </p>
                <button
                  onClick={() => setActiveFilter('all')}
                  className="mt-3 text-sm font-medium text-rgpd-blue transition-colors hover:text-rgpd-blue-dark"
                >
                  Afficher toutes les newsletters
                </button>
              </div>
            ) : (
              <>
                <div className="mb-4 text-sm text-gray-600">
                  {filteredNewsletters.length} newsletter(s) disponible(s){activeFilter !== 'all' ? ` pour « ${NEWSLETTER_TYPE_LABELS[activeFilter]} »` : ''}
                </div>
                <MUITable
                  data={tableData}
                  headers={headers}
                  isSearchable
                  sortableColumns={['Titre', 'Disponible depuis', 'Type']}
                />
              </>
            )}
            
            {/* Overlay when no template is selected */}
            {!company?.brandingSettings?.defaultTemplate && (
              <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/50">
                <div className="max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
                  <InformationCircleIcon className="mx-auto mb-4 h-16 w-16 text-yellow-500" />
                  <h3 className="mb-4 text-xl font-semibold">Choix du modèle de newsletter requis</h3>
                  <p className="mb-6 text-gray-600">
                    Vous devez d'abord choisir un modèle de newsletter pour pouvoir consulter vos newsletters.
                  </p>
                  <button
                    onClick={() => navigate(ROUTES.COMMUNICATION_NEWSLETTER_TEMPLATE_SELECTION)}
                    className="rounded-lg bg-rgpd-blue px-6 py-3 text-white transition-colors hover:bg-rgpd-blue-dark"
                  >
                    Choisir mon modèle de newsletter
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default NewsletterList;